'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { acceptWaiver, getWaiverContent, getWaiverStatus } from '@src/actions/waiver.actions';
import { useLocale } from '@src/i18n/LocaleProvider';
import { log } from '@src/utils/log.util';
import { instrumentAction } from "@src/utils/instrumentation";
export interface WaiverGateArgs {
  videoGUID: string;
  brandCode?: string;
}
interface WaiverModalState {
  content?: string;
  onAccept: () => void;
  onDecline: () => void;
}
export function useVideoWaiver() {
  const $ = _c(9);
  const {
    locale
  } = useLocale();
  const [waiverModal, setWaiverModal] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const noWaiverRequiredRef = useRef(t0);
  const waiverContentRef = useRef(undefined);
  const hasFetchedContentRef = useRef(false);
  let t1;
  if ($[1] !== locale) {
    t1 = async () => {
      if (!hasFetchedContentRef.current) {
        hasFetchedContentRef.current = true;
        waiverContentRef.current = await instrumentAction("WaiverActions.getWaiverContent", getWaiverContent)({
          locale: locale.codeSnakeCase
        });
      }
      return waiverContentRef.current;
    };
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const ensureWaiverContent = t1;
  let t2;
  if ($[3] !== ensureWaiverContent || $[4] !== locale) {
    t2 = async args => {
      const {
        videoGUID,
        brandCode
      } = args;
      if (!videoGUID) {
        return true;
      }
      if (noWaiverRequiredRef.current.has(videoGUID)) {
        return true;
      }
      const {
        isWaiverRequired
      } = await instrumentAction("WaiverActions.getWaiverStatus", getWaiverStatus)({
        videoGUID,
        brandCode,
        locale: locale.codeSnakeCase
      });
      if (!isWaiverRequired) {
        noWaiverRequiredRef.current.add(videoGUID);
        return true;
      }
      const content = await ensureWaiverContent();
      return new Promise(resolve => {
        setWaiverModal({
          content,
          onAccept: () => {
            noWaiverRequiredRef.current.add(videoGUID);
            instrumentAction("WaiverActions.acceptWaiver", acceptWaiver)({
              videoGUID,
              brandCode,
              locale: locale.codeSnakeCase
            }).catch(_temp);
            setWaiverModal(null);
            resolve(true);
          },
          onDecline: () => {
            setWaiverModal(null);
            resolve(false);
          }
        });
      });
    };
    $[3] = ensureWaiverContent;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const ensureWaiverAccepted = t2;
  let t3;
  if ($[6] !== ensureWaiverAccepted || $[7] !== waiverModal) {
    t3 = {
      ensureWaiverAccepted,
      waiverModal
    };
    $[6] = ensureWaiverAccepted;
    $[7] = waiverModal;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(error) {
  return log.error("[useVideoWaiver] Failed to record waiver acceptance", error);
}